import { connect, useDispatch } from 'react-redux';
import { Chip, Tooltip } from '@mui/material';
import { Filter, Info } from 'lucide-react';
import { CheckPicker } from 'rsuite';
import drawerActions from '@/Actions/drawer.actions';
import { useAuth } from '@/Components/Auth/Login/auth';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import { filtersInfoChip } from '@/Helpers';
import NotAccessiblePage from '@/Pages/NotAccessable/NotAccessable';
import StatsComp from '@/Pages/ProductionAnalysis/StatsComp';
import {
	FINDER_DEFAULT_STATE,
	TABS,
	getKpiCards,
	isAnnotationDashboardUser,
} from './annotationProductivity.config';
import { useAnnotationProductivityStore } from './annotationProductivityStore';
import ProductivityTable from './components/ProductivityTable';

/**
 * Annotation Productivity — internal, cross-customer.
 *
 * Backed by GET /v2.1/annotation/dashboard/productivity: one row per annotator
 * per customer, across every customer, so the page needs no per-customer
 * fan-out. Data is D-1 and covers completed annotation only, which is why
 * nothing here shows partial progress.
 */
const formatInt = (value) => (value ?? 0).toLocaleString('en-IN');

function AnnotationProductivityPage(props) {
	const { userPermissions } = props;
	const { userConfig = {} } = useAuth();
	const reduxDispatch = useDispatch();

	const roleValues = Object.values(userConfig?.userRolesDict ?? {});
	const isInternal = userPermissions?.INTERNAL ||
		roleValues.includes('INTERNAL') ||
		roleValues.includes('CXO Office');
	// INTERNAL alone is not enough: the endpoint is staff-only, so an internal
	// user at a customer would see the page and then fail every request.
	const canViewDashboard = isInternal && isAnnotationDashboardUser(userConfig);

	const {
		asOf,
		loading,
		loadFailed,
		accessDenied,
		activeTab,
		appliedFilters,
		annotatorFilter,
		setActiveTab,
		setAnnotatorFilter,
		applyFinderFilters,
		getVisibleRows,
		getTotals,
		getFilterOptions,
	} = useAnnotationProductivityStore();

	const visibleRows = getVisibleRows();
	const totals = getTotals();
	const { customers, annotators } = getFilterOptions();

	const filtersInfo = {
		// Omitting a key disables that accordion. `store: false` would *enable*
		// and auto-expand an empty Store panel -- the flag reads as
		// "this page has content there".
		header: {
			date: false,
			statusAndTypes: false,
		},
		dateRange: {
			show: true,
			text: 'Report Date',
		},
		customExclusiveFilters: {
			show: true,
			text: 'Customer',
			multiSelect: true,
			filterList: customers,
		},
	};

	/**
	 * The shared drawer has no annotator filter and only one generic slot, which
	 * Customer already uses — so this rides in through headerComponent. It
	 * applies on change rather than on Apply; unifying that needs an annotator
	 * filter inside ImagesFinder itself.
	 */
	const renderAnnotatorFilter = () => {
		return (
			<div className='flex flex-col gap-1 border-b border-[#00000033] px-4 py-3'>
				<span className='text-xs font-semibold uppercase tracking-wide text-slate-500'>
					Annotator
				</span>
				<CheckPicker
					block
					data={annotators}
					value={annotatorFilter}
					placeholder='All annotators'
					menuClassName='z-[99999]'
					container={() => document.body}
					onChange={(value) => setAnnotatorFilter(value ?? [])}
				/>
			</div>
		);
	};

	if (!canViewDashboard) {
		return <NotAccessiblePage />;
	}

	return (
		<div className='flex h-[calc(100vh-50px)] w-full flex-col items-stretch gap-3 overflow-auto bg-slate-50 p-4'>
			{/* The drawer auto-submits on mount; that is what loads the page. */}
			<ImagesFinder
				onSubmit={applyFinderFilters}
				defaultState={FINDER_DEFAULT_STATE}
				filtersInfo={filtersInfo}
				headerComponent={renderAnnotatorFilter}
				hideDateRangeTimeType
				exportInfo={{
					hideExportAccordian: true,
				}}
			/>

			<div className='flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4'>
				<div className='flex flex-wrap items-center gap-3'>
					<p className='m-0 text-[20px] font-bold text-gray-900'>
						Annotation Productivity
					</p>
					<div className='ml-2 flex w-fit items-center gap-1 rounded-lg bg-gray-100 p-1'>
						{TABS.map((tab) => {
							const isActive = activeTab === tab.key;
							return (
								<button
									key={tab.key}
									type='button'
									onClick={() => setActiveTab(tab.key)}
									className={`rounded-md px-4 py-1.5 text-[13px] transition-all ${isActive
										? 'bg-white font-bold text-gray-900 shadow-sm'
										: 'font-semibold text-gray-500 hover:text-gray-700'}`}
								>
									{tab.label}
								</button>
							);
						})}
					</div>
					<div className='ml-auto flex items-center gap-2'>
						<div className='[&>div]:!m-0 [&_*]:!text-[11px]'>
							{filtersInfoChip(filtersInfo, appliedFilters)}
						</div>
						{annotatorFilter.length > 0 && (
							<Chip
								size='small'
								label={`Annotator: ${annotatorFilter.length} selected`}
								onDelete={() => setAnnotatorFilter([])}
								className='!h-6 !bg-slate-100 !text-[11px] !font-semibold !text-slate-700'
							/>
						)}
						<button
							type='button'
							onClick={() => reduxDispatch(drawerActions.toggleFiltersDrawer(true))}
							className='flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-teal-700'
						>
							Filter
							<Filter size={13} />
						</button>
					</div>
				</div>
				<div className='flex flex-wrap items-center gap-2'>
					{asOf && (
						<Chip
							label={`Data as of ${asOf}`}
							className='!h-6 !bg-blue-50 !px-1 !text-xs !font-semibold !text-blue-800'
						/>
					)}
					<Tooltip title='Refreshed daily. Filtered on report date (inclusive) — the business day the work is booked to. Completed annotation only.'>
						<span
							className='flex cursor-help items-center text-slate-400 hover:text-slate-600'
							aria-label='How this data is compiled'
						>
							<Info size={15} />
						</span>
					</Tooltip>
				</div>
			</div>

			<StatsComp statsCards={getKpiCards(activeTab, totals, formatInt)} />

			<ProductivityTable
				activeTab={activeTab}
				rows={visibleRows}
				loading={loading}
				loadFailed={loadFailed}
				accessDenied={accessDenied}
			/>
		</div>
	);
}

const mapStateToProps = (state) => {
	return {
		userPermissions: state.navigation.userPermissions,
	};
};

export default connect(mapStateToProps)(AnnotationProductivityPage);
